import { Component, OnInit, OnDestroy, inject, signal, computed, Input, Output, EventEmitter, effect, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { combineLatest, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { DepartmentSchemesService, DepartmentEntry } from '../../services/department-schemes.service';
import { UserRole } from '../../models/user.model';
import { Project } from '../../models/project.model';
import { Firestore, collection, collectionData, doc, getDoc, query, where } from '@angular/fire/firestore';
import { UserProfileDisplayService } from '../../services/user-profile-display.service';

type SidebarPanel = 'reports' | 'analytics' | 'documents' | 'alerts' | 'settings' | 'help' | null;

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
    @Input() isOpen: boolean = false; // Mobile sidebar state
    @Input() isCollapsed: boolean = false; // Desktop collapse state
    @Output() closeSidebar = new EventEmitter<void>();

    stuckCount = signal(0);
    isLoggingOut = signal(false);

    // Live project stream for Minister panels
    allProjects = signal<Project[]>([]);
    activePanel = signal<SidebarPanel>(null);

    // ── Minister panel-specific computed signals ──
    reportRows = computed(() => this.allProjects());
    analyticsStats = computed(() => {
        const p = this.allProjects();
        const total = p.length || 1;
        return {
            total: p.length,
            sanctioned: p.reduce((s, x) => s + (x.cost || 0), 0),
            spent: p.reduce((s, x) => s + (x.spent || 0), 0),
            avgProgress: Math.round(p.reduce((s, x) => s + (x.physical || 0), 0) / total),
            completed: p.filter(x => x.status === 'Completed').length,
            inProgress: p.filter(x => x.status === 'In Progress').length,
            stuck: p.filter(x => x.status === 'Stuck').length,
            planned: p.filter(x => x.status === 'Planned').length
        };
    });
    documentsRows = computed(() =>
        this.allProjects().filter(p => (p.photos?.length ?? 0) > 0 || p.beforeImageUrl || p.currentImageUrl)
    );
    alertItems = computed(() => {
        const today = new Date();
        const p = this.allProjects();
        const delayed = p.filter(x => !!x.isDelayed || x.status === 'Stuck');
        const hmPending = p.filter(x => x.hmPriority && x.status !== 'Completed');
        const lowProgress = p.filter(x => (x.physical || 0) < 30 && x.status !== 'Completed' && x.status !== 'Planned');
        const overdue60 = p.filter(x => {
            if (x.status === 'Completed') return false;
            const t = new Date(x.targetDate || x.end);
            if (isNaN(t.getTime())) return false;
            return Math.floor((today.getTime() - t.getTime()) / 86400000) > 60;
        });
        return { delayed, hmPending, lowProgress, overdue60 };
    });
    alertCount = computed(() => {
        const a = this.alertItems();
        return a.delayed.length + a.hmPending.length + a.overdue60.length;
    });

    panelTitle = computed(() => {
        switch (this.activePanel()) {
            case 'reports':   return 'Reports';
            case 'analytics': return 'Analytics';
            case 'documents': return 'Project Documents & Photos';
            case 'alerts':    return 'Alerts & Notifications';
            case 'settings':  return 'Settings';
            case 'help':      return 'Quick Help';
            default:          return '';
        }
    });

    private authService = inject(AuthService);
    readonly profileUi = inject(UserProfileDisplayService);
    private firestoreService = inject(FirestoreProjectService);
    private deptService = inject(DepartmentSchemesService);
    private firestore = inject(Firestore);
    private router = inject(Router);
    private injector = inject(Injector);
    private destroy$ = new Subject<void>();
    mappedDepartmentIds = signal<string[]>([]);
    private deptAssignmentsSub: Subscription | null = null;

    currentUser = computed(() => this.authService.currentUser());
    isAdmin = this.authService.isAdmin;
    isOperator = this.authService.isOperator;
    isApprover = this.authService.isApprover;
    isMinister = this.authService.isMinister;
    isOsd = this.authService.isOsd;
    isMinisterOrOsd = this.authService.isMinisterOrOsd;

    /**
     * Departments assigned to the logged-in operator OR approver.
     * Renders one sidebar link per assigned dept for both roles.
     *
     * Matching: `departmentIds` are stored as normalized slugs
     * ("works", "water_resources", …). We map them back to the master
     * dept entries so the sidebar always shows the canonical name + icon.
     */
    assignedDepartments = computed<DepartmentEntry[]>(() => {
        const user = this.currentUser();
        if (!user) return [];
        if (user.role !== UserRole.OPERATOR && user.role !== UserRole.APPROVER) return [];
        const ids = new Set([
            ...(user.departmentIds ?? []),
            ...(user.assignedDepartments ?? []),
            ...(this.mappedDepartmentIds() ?? [])
        ].map(v => this.deptIdOf(v)));
        if (ids.size === 0) return [];
        return this.deptService.getDepartments().filter(d =>
            ids.has(this.deptIdOf(d.name)) || ids.has(this.deptIdOf(d.shortName))
        );
    });

    private deptIdOf(name: string): string {
        return name.toLowerCase()
            .replace(/[\s&]/g, '_')
            .replace(/[^a-z0-9_]/g, '')
            .replace(/_+/g, '_');
    }

    private async resolveDepartmentIdsToNames(rawIds: string[]): Promise<string[]> {
        const all = this.deptService.getDepartments();
        const byNormalizedName = new Map(all.map(d => [this.deptIdOf(d.name), d.name]));
        const byNormalizedShort = new Map(all.map(d => [this.deptIdOf(d.shortName), d.name]));
        const resolved: string[] = [];
        const unknown: string[] = [];

        for (const raw of rawIds) {
            const key = this.deptIdOf(raw);
            const knownName = byNormalizedName.get(key) || byNormalizedShort.get(key);
            if (knownName) {
                resolved.push(knownName);
            } else {
                unknown.push(raw);
            }
        }

        if (unknown.length > 0) {
            const fetchedNames = await Promise.all(unknown.map(async (deptDocId) => {
                try {
                    const snap = await getDoc(doc(this.firestore, 'departments', deptDocId));
                    return snap.exists() ? String(snap.data()?.['name'] || '').trim() : '';
                } catch {
                    return '';
                }
            }));
            resolved.push(...fetchedNames.filter(Boolean));
        }

        return Array.from(new Set(resolved));
    }

    ngOnInit(): void {
        this.subscribeDepartmentAssignments();

        this.firestoreService.getStuckProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: stuck => this.stuckCount.set(stuck.length),
                error: err => console.warn('Stuck projects stream error:', err)
            });

        this.firestoreService.getHmProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: projs => this.allProjects.set(projs),
                error: err => console.warn('HM projects stream error:', err)
            });
    }

    ngOnDestroy(): void {
        this.deptAssignmentsSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeDepartmentAssignments(): void {
        effect(() => {
            const user = this.currentUser();
            this.deptAssignmentsSub?.unsubscribe();
            this.mappedDepartmentIds.set([]);

            if (!user || (user.role !== UserRole.OPERATOR && user.role !== UserRole.APPROVER)) {
                return;
            }

            const uid = user.uid;
            const firebaseUid = this.authService.getFirebaseAuthUid();
            const candidateUserIds = Array.from(new Set([uid, firebaseUid].filter(Boolean) as string[]));
            if (candidateUserIds.length === 0) return;

            const streams = candidateUserIds.map((candidateId) => {
                const q = query(
                    collection(this.firestore, 'departmentAssignments'),
                    where('userId', '==', candidateId),
                    where('isActive', '==', true)
                );
                return collectionData(q, { idField: 'id' }) as any;
            });

            this.deptAssignmentsSub = combineLatest(streams).subscribe({
                next: async (rowsPerSource: any[][]) => {
                    const ids = rowsPerSource
                        .flat()
                        .map(r => String(r?.departmentId || '').trim())
                        .filter(Boolean);
                    const normalizedNames = await this.resolveDepartmentIdsToNames(Array.from(new Set(ids)));
                    this.mappedDepartmentIds.set(normalizedNames);
                },
                error: () => this.mappedDepartmentIds.set([])
            });
        }, { injector: this.injector });
    }

    onLinkClick(): void {
        this.closeSidebar.emit();
    }

    triggerAction(route: string, action: string): void {
        this.router.navigate([route], { queryParams: { action } });
        this.closeSidebar.emit();
    }

    openPanel(panel: SidebarPanel): void {
        this.activePanel.set(panel);
        this.closeSidebar.emit();
    }

    closePanel(): void {
        this.activePanel.set(null);
    }

    /** Reports → Export current project list to CSV. */
    exportReportCsv(): void {
        const rows = this.reportRows();
        if (!rows.length) return;
        const head = ['Name', 'Department', 'Location', 'Status', 'Progress%', 'Cost(L)', 'Spent(L)'];
        const csv  = [
            head.join(','),
            ...rows.map(p => [
                `"${(p.name || '').replace(/"/g, '""')}"`,
                p.dept || '',
                `"${(p.loc || '').replace(/"/g, '""')}"`,
                p.status || '',
                p.physical ?? 0,
                p.cost ?? 0,
                p.spent ?? 0
            ].join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `ppms-projects-report-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    goToUserManagement(): void {
        this.closePanel();
        this.router.navigate(['/dhamnagar-dashboard/user-management']);
        this.closeSidebar.emit();
    }

    async logout(): Promise<void> {
        this.isLoggingOut.set(true);

        try {
            console.log('Sidebar: Initiating logout...');
            await this.authService.logout();
            console.log('Sidebar: Logout successful!');
        } catch (error) {
            console.error('Logout error:', error);
            this.isLoggingOut.set(false);
            window.location.href = '/login';
        }
    }
}
